// Pure client-side aggregation for the Analytics surface. Kept dependency-free
// so the bucketing and rollups are unit-testable without a database.

/** Count items by their `status`, most frequent first. */
export function countByStatus<T extends { status: string }>(items: T[]): { status: string; count: number }[] {
  const m = new Map<string, number>();
  for (const it of items) m.set(it.status, (m.get(it.status) ?? 0) + 1);
  return [...m.entries()].map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
}

/** Count items into the last `weeks` weekly buckets (oldest → newest). */
export function bucketByWeek<T extends { created_at: string }>(
  items: T[],
  weeks = 8,
  now = Date.now(),
): { label: string; count: number }[] {
  const WEEK = 7 * 864e5;
  const buckets = Array.from({ length: weeks }, (_, i) => {
    const end = now - (weeks - 1 - i) * WEEK;
    return { count: 0, label: new Date(end).toLocaleDateString("en-US", { month: "short", day: "numeric" }) };
  });
  for (const it of items) {
    const t = new Date(it.created_at).getTime();
    if (!Number.isFinite(t)) continue;
    const idx = Math.floor((now - t) / WEEK);
    if (idx >= 0 && idx < weeks) buckets[weeks - 1 - idx].count += 1;
  }
  return buckets;
}

/** Sum a numeric field across items. */
export function sumBy<T>(items: T[], pick: (t: T) => number | null | undefined): number {
  return items.reduce((acc, it) => acc + (pick(it) ?? 0), 0);
}

/** Win rate as a 0–100 integer percentage. */
export function winRate(total: number, wins: number): number {
  return total > 0 ? Math.round((wins / total) * 100) : 0;
}

/** Shape of the server KPI summary (structural, to keep this module dependency-free). */
export interface KpiSummaryLike {
  jobs: { posted: number; open: number };
  applications: { received: number; hires: number; hire_rate: number; sent: number };
  loads: { posted: number; awarded: number };
  bids: { placed: number; won: number; win_rate: number };
  activity_30d: number;
}

/**
 * Curate the server KPI summary into a display list. Drops dimensions that are
 * entirely empty for this profile (a recruiter sees no bid metrics, a driver no job
 * metrics) but always keeps recent activity, and falls back to the full set if the
 * profile is brand new (fewer than 3 non-empty KPIs).
 */
export function kpis(s: KpiSummaryLike): { label: string; value: string | number }[] {
  const all = [
    { label: "Jobs posted", value: s.jobs.posted as string | number, empty: s.jobs.posted === 0 },
    { label: "Applications", value: s.applications.received, empty: s.applications.received === 0 },
    { label: "Hire rate", value: `${s.applications.hire_rate}%`, empty: s.applications.received === 0 },
    { label: "Apps sent", value: s.applications.sent, empty: s.applications.sent === 0 },
    { label: "Loads posted", value: s.loads.posted, empty: s.loads.posted === 0 },
    { label: "Loads awarded", value: s.loads.awarded, empty: s.loads.posted === 0 },
    { label: "Bids placed", value: s.bids.placed, empty: s.bids.placed === 0 },
    { label: "Win rate", value: `${s.bids.win_rate}%`, empty: s.bids.placed === 0 },
    { label: "Activity (30d)", value: s.activity_30d, empty: false },
  ];
  const kept = all.filter((k) => !k.empty);
  return (kept.length >= 3 ? kept : all).map(({ label, value }) => ({ label, value }));
}
